import { ChevronRightIcon, FileImageIcon, FileText, FolderIcon, Loader2Icon } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@folio/components/ui/badge';
import { Group, Stack } from '@folio/components/ui/flex';
import { Text } from '@folio/components/ui/text';

type ReviewListDocument = { id: string; name: string };

const IMAGE_EXTENSIONS = new Set(['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'heic']);

function FileFormatIcon({ name }: { name: string }) {
  const extension = name.split('.').pop()?.toLowerCase();
  return extension && IMAGE_EXTENSIONS.has(extension) ? (
    <FileImageIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
  ) : (
    <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
  );
}

function ReviewGroup<TDocument extends ReviewListDocument>({
  title,
  documents,
  action,
  getTitle,
  renderBadges,
  onView,
}: {
  title: string;
  documents: TDocument[];
  action?: ReactNode;
  getTitle: (document: TDocument) => string;
  renderBadges: (document: TDocument) => ReactNode;
  onView: (document: TDocument) => void;
}) {
  return (
    <section aria-label={title}>
      <Group justify="between" gap={3} className="border-b px-3 py-3">
        <Group gap={2}>
          <Text inline size="xs" weight="semibold" className="uppercase tracking-wide">
            {title}
          </Text>
          <Badge variant="light" size="sm" tone="muted" className="tabular-nums">
            {documents.length}
          </Badge>
        </Group>
        {action}
      </Group>
      {documents.map(document => (
        <Group
          key={document.id}
          asChild
          justify="between"
          gap={3}
          className="w-full border-b px-3 py-2.5 text-left transition-colors hover:bg-slate-50"
        >
          <button type="button" onClick={() => onView(document)}>
            <Group gap={2.5} className="min-w-0">
              <FileFormatIcon name={document.name} />
              <Stack gap={0} className="min-w-0">
                <Text size="sm" weight="semibold" className="break-words">
                  {getTitle(document)}
                </Text>
                <Text size="xs" tone="muted" className="break-words">
                  {document.name}
                </Text>
              </Stack>
            </Group>
            <Group gap={2} className="shrink-0">
              {renderBadges(document)}
              <ChevronRightIcon className="h-4 w-4 text-muted-foreground" />
            </Group>
          </button>
        </Group>
      ))}
    </section>
  );
}

export function ToValidateList<TDocument extends ReviewListDocument>({
  flagged,
  conform,
  processingCount,
  acceptAll,
  getTitle,
  renderBadges,
  onView,
}: {
  flagged: TDocument[];
  conform: TDocument[];
  processingCount: number;
  acceptAll?: ReactNode;
  getTitle: (document: TDocument) => string;
  renderBadges: (document: TDocument) => ReactNode;
  onView: (document: TDocument) => void;
}) {
  const { t } = useTranslation();
  const groupProps = { getTitle, renderBadges, onView };

  return (
    <Stack gap={4}>
      {flagged.length > 0 && (
        <ReviewGroup
          title={t('documentReview.toValidate.flagged')}
          documents={flagged}
          {...groupProps}
        />
      )}
      {conform.length > 0 && (
        <ReviewGroup
          title={t('documentReview.toValidate.conform')}
          documents={conform}
          action={acceptAll}
          {...groupProps}
        />
      )}
      {processingCount > 0 && (
        <Group gap={2} className="px-3 text-muted-foreground">
          <Loader2Icon className="h-4 w-4 animate-spin" />
          <Text size="sm" tone="muted">
            {t('documentReview.toValidate.processing', { count: processingCount })}
          </Text>
        </Group>
      )}
    </Stack>
  );
}

export function MissingList({
  categories,
  renderAddButton,
}: {
  categories: { category: string; label: string; status: string }[];
  renderAddButton?: () => ReactNode;
}) {
  return (
    <Stack gap={0}>
      {categories.map(({ category, label, status }) => (
        <Group key={category} justify="between" gap={3} className="border-b px-3 py-3">
          <Group gap={2} className="min-w-0">
            <FolderIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
            <Text size="sm" weight="semibold" className="break-words">
              {label}
            </Text>
          </Group>
          <Group gap={3} className="shrink-0">
            <Text inline size="sm" tone="muted">
              {status}
            </Text>
            {renderAddButton?.()}
          </Group>
        </Group>
      ))}
    </Stack>
  );
}
